import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonFunction } from 'src/app/helpers/commonFunction';
import { MeetingMasterService } from 'src/app/services/meetingMaster.service';
import { MinuteVoteService } from 'src/app/services/minute-vote.service';

@Component({
  selector: 'app-minute-vote-detail',
  templateUrl: './minute-vote-detail.component.html',
  styleUrls: ['./minute-vote-detail.component.css'],
})
export class MinuteVoteDetailComponent implements OnInit, OnChanges {
  @Input() meetingId: string;
  /** DRAFT or FINAL */
  @Input() minuteType: 'DRAFT' | 'FINAL' = 'DRAFT';
  @Input() allowVoting = true;
  /** Optional section title */
  @Input() heading: string;

  loading = false;
  votingLoading = false;
  error: string | null = null;
  summary: {
    approveCount: number;
    rejectCount: number;
    totalVotes: number;
    approveVoters: any[];
    rejectVoters: any[];
    userVote: { voteType: string; votedAt: any; comments: string } | null;
  } | null = null;

  rejectComment = '';
  showBreakdown = false;
  meetingParticipants: Array<{ userId: string; userName: string; userEmail: string; participantType: string }> = [];
  adminPanelOpen = false;
  adminSaving = false;
  isAdmin = false;
  currentUserId = '';
  eligibleUserIds = new Set<string>();

  constructor(
    private minuteVoteService: MinuteVoteService,
    private commonFunction: CommonFunction,
    private meetingMasterService: MeetingMasterService
  ) {}

  safeReadable(value: any): string {
    const raw = value == null ? '' : String(value);
    if (!raw) {
      return '';
    }
    try {
      const dec = this.commonFunction.decrypt(raw);
      if (dec && dec.trim()) {
        return dec.trim();
      }
    } catch {}
    return raw;
  }

  ngOnInit(): void {
    this.resolveCurrentUserAndRoles();
    if (!this.allowVoting) {
      this.showBreakdown = true;
    }
    this.loadMeetingParticipants();
    this.loadSummary();
    this.loadEligibility();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['allowVoting'] && !this.allowVoting) {
      this.showBreakdown = true;
    }
    if (ch['meetingId'] || ch['minuteType']) {
      this.loadMeetingParticipants();
      this.loadSummary();
      this.loadEligibility();
    }
  }

  get title(): string {
    if (this.heading) {
      return this.heading;
    }
    return this.minuteType === 'FINAL' ? 'Final minutes voting' : 'Draft minutes voting';
  }

  loadSummary(): void {
    if (!this.meetingId || !this.minuteType) {
      this.summary = null;
      return;
    }
    this.loading = true;
    this.error = null;
    this.minuteVoteService
      .getMinuteVoteSummary(this.meetingId, this.minuteType)
      .then((res: any) => {
        this.loading = false;
        if (res != null && res.status == 0 && res.data) {
          const d = res.data;
          this.summary = {
            approveCount: d.approveCount || 0,
            rejectCount: d.rejectCount || 0,
            totalVotes: d.totalVotes ?? (d.approveCount || 0) + (d.rejectCount || 0),
            approveVoters: d.approveVoters || [],
            rejectVoters: d.rejectVoters || [],
            userVote: d.userVote || null,
          };
        } else {
          this.summary = null;
          this.error = res?.message || 'Could not load minute voting data.';
        }
      })
      .catch(() => {
        this.loading = false;
        this.summary = null;
        this.error = 'Could not load minute voting data.';
      });
  }

  submitVote(voteType: 'APPROVE' | 'REJECT'): void {
    if (!this.allowVoting || !this.meetingId) {
      return;
    }
    if (!this.canCurrentUserVote()) {
      alert('You are currently not eligible to vote on these minutes.');
      return;
    }
    if (voteType === 'REJECT' && !this.rejectComment.trim()) {
      alert('Please add a comment explaining your rejection (required).');
      return;
    }
    this.votingLoading = true;
    this.error = null;
    const payload = {
      MeetingID: this.meetingId,
      MinuteType: this.minuteType,
      VoteType: voteType,
      Comments: voteType === 'REJECT' ? this.rejectComment.trim() : null,
    };
    this.minuteVoteService
      .submitMinuteVote(payload)
      .then((res: any) => {
        this.votingLoading = false;
        if (res != null && res.status == 0) {
          this.rejectComment = '';
          this.loadSummary();
        } else {
          this.error = res?.message || 'Failed to submit vote.';
        }
      })
      .catch(() => {
        this.votingLoading = false;
        this.error = 'Failed to submit vote.';
      });
  }

  retractVote(): void {
    if (!this.allowVoting || !this.meetingId || !confirm('Retract your vote on these minutes?')) {
      return;
    }
    this.votingLoading = true;
    this.minuteVoteService
      .retractMinuteVote(this.meetingId, this.minuteType)
      .then((res: any) => {
        this.votingLoading = false;
        if (res != null && res.status == 0) {
          this.rejectComment = '';
          this.loadSummary();
        } else {
          this.error = res?.message || 'Failed to retract vote.';
        }
      })
      .catch(() => {
        this.votingLoading = false;
        this.error = 'Failed to retract vote.';
      });
  }

  allVoters(): Array<{
    userName: string;
    userEmail: string;
    votedAt: any;
    comments: string | null;
    voteLabel: string;
  }> {
    if (!this.summary) {
      return [];
    }
    const a = (this.summary.approveVoters || []).map((v) => ({
      userName: v.userName || '—',
      userEmail: this.safeReadable(v.userEmail || ''),
      votedAt: v.votedAt,
      comments: this.safeReadable(v.comments || '') || null,
      voteLabel: 'Approve',
    }));
    const r = (this.summary.rejectVoters || []).map((v) => ({
      userName: v.userName || '—',
      userEmail: this.safeReadable(v.userEmail || ''),
      votedAt: v.votedAt,
      comments: this.safeReadable(v.comments || '') || null,
      voteLabel: 'Reject',
    }));
    return [...a, ...r].sort((x, y) => (x.userName || '').localeCompare(y.userName || ''));
  }

  initials(name: string): string {
    if (!name || typeof name !== 'string') {
      return '?';
    }
    const p = name.trim().split(/\s+/).filter(Boolean);
    if (p.length === 0) {
      return '?';
    }
    if (p.length === 1) {
      return p[0].charAt(0).toUpperCase();
    }
    return (p[0].charAt(0) + p[p.length - 1].charAt(0)).toUpperCase();
  }

  private resolveCurrentUserAndRoles(): void {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      this.currentUserId = String(user?._id || '').trim();
      const roles = Array.isArray(user?.Roles) ? user.Roles.map((r) => String(r?.RoleName || '').trim().toLowerCase()) : [];
      this.isAdmin = roles.includes('admin') || roles.includes('all role');
    } catch {
      this.currentUserId = '';
      this.isAdmin = false;
    }
  }

  canCurrentUserVote(): boolean {
    if (!this.allowVoting) {
      return false;
    }
    if (!this.currentUserId) {
      return true;
    }
    if (this.eligibleUserIds.size === 0) {
      return true;
    }
    return this.eligibleUserIds.has(this.currentUserId);
  }

  private loadMeetingParticipants(): void {
    if (!this.meetingId) {
      this.meetingParticipants = [];
      return;
    }
    this.meetingMasterService
      .getMeetingById(this.meetingId)
      .then((res: any) => {
        const meeting = res?.meetingData?.[0] || res?.data?.[0] || res?.data || {};
        const directors = Array.isArray(meeting?.Directors) ? meeting.Directors : [];
        const members = Array.isArray(meeting?.Members) ? meeting.Members : [];
        this.meetingParticipants = [...this.toParticipants(directors, 'Director'), ...this.toParticipants(members, 'Member')];
        if (this.eligibleUserIds.size === 0 && this.meetingParticipants.length > 0) {
          this.eligibleUserIds = new Set(this.meetingParticipants.map((p) => p.userId));
        }
      })
      .catch(() => {
        this.meetingParticipants = [];
      });
  }

  private toParticipants(rows: any[], participantType: string): Array<{ userId: string; userName: string; userEmail: string; participantType: string }> {
    return (rows || [])
      .map((r: any) => {
        const userId = String(r?.UserGovevaId || r?.userId || r?._id || '').trim();
        const userName = String(
          r?.Name ||
            r?.DirectorName ||
            r?.MemberName ||
            `${r?.FirstName || ''} ${r?.LastName || ''}` ||
            r?.EmailId ||
            r?.EmailID ||
            '—'
        ).trim();
        const userEmail = this.safeReadable(r?.EmailId || r?.EmailID || r?.email || '');
        return { userId, userName: userName || '—', userEmail, participantType };
      })
      .filter((x) => !!x.userId);
  }

  loadEligibility(): void {
    if (!this.meetingId || !this.minuteType) {
      return;
    }
    this.minuteVoteService
      .getMinuteVoteEligibility(this.meetingId, this.minuteType)
      .then((res: any) => {
        const data = res?.data ?? res?.result ?? res ?? {};
        const incoming = Array.isArray(data?.eligibleUserIds) ? data.eligibleUserIds : [];
        if (incoming.length > 0) {
          this.eligibleUserIds = new Set(incoming.map((x: any) => String(x).trim()).filter(Boolean));
        } else if (this.meetingParticipants.length > 0) {
          this.eligibleUserIds = new Set(this.meetingParticipants.map((p) => p.userId));
        }
      })
      .catch(() => {
        if (this.meetingParticipants.length > 0 && this.eligibleUserIds.size === 0) {
          this.eligibleUserIds = new Set(this.meetingParticipants.map((p) => p.userId));
        }
      });
  }

  isParticipantEligible(userId: string): boolean {
    const uid = String(userId || '').trim();
    if (!uid) {
      return false;
    }
    if (this.eligibleUserIds.size === 0) {
      return true;
    }
    return this.eligibleUserIds.has(uid);
  }

  toggleParticipantEligibility(userId: string): void {
    const uid = String(userId || '').trim();
    if (!uid) {
      return;
    }
    if (this.eligibleUserIds.has(uid)) {
      this.eligibleUserIds.delete(uid);
    } else {
      this.eligibleUserIds.add(uid);
    }
    this.eligibleUserIds = new Set(this.eligibleUserIds);
  }

  saveEligibility(): void {
    if (!this.isAdmin || !this.meetingId || !this.minuteType) {
      return;
    }
    const eligibleUserIds = Array.from(this.eligibleUserIds);
    this.adminSaving = true;
    this.minuteVoteService
      .updateMinuteVoteEligibility(this.meetingId, this.minuteType, { eligibleUserIds })
      .then((res: any) => {
        this.adminSaving = false;
        if (res != null && res.status == 0) {
          alert('Voting eligibility updated successfully.');
          this.loadSummary();
          return;
        }
        alert(res?.message || 'Failed to update voting eligibility.');
      })
      .catch(() => {
        this.adminSaving = false;
        alert('Failed to update voting eligibility.');
      });
  }
}
